import React, { useState } from "react";

interface WanPromptGuidanceProps {
  generationProvider?: "wan" | "facefusion" | string;
  style?: string;
  className?: string;
}

export function WanPromptGuidance({
  generationProvider = "wan",
  style = "photo",
  className = "",
}: WanPromptGuidanceProps) {
  const [expanded, setExpanded] = useState(false);

  if (generationProvider !== "wan") {
    return (
      <div className={`mt-1 text-xs text-gray-400 ${className}`}>
        {style === "chibi"
          ? "卡通/IP风格提示词：应描述如何保留模板的构图、动作、服装和道具，同时融合用户的外貌特征进行生成。"
          : "纯换脸风格提示词：应描述如何保留模板的身体、服装、背景和光影，在此基础上进行人脸融合。"}
      </div>
    );
  }

  return (
    <div className={`mt-2 space-y-2 text-xs ${className}`}>
      {/* Four-stage prompt pipeline summary */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2.5 text-gray-700">
        <div className="flex items-center justify-between text-slate-800 font-medium">
          <span className="flex items-center gap-1.5 font-semibold text-teal-700">
            <i className="ri-flow-chart text-sm" />
            万相提示词四段式构成说明
          </span>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-teal-600 hover:text-teal-700 flex items-center gap-0.5 font-normal cursor-pointer"
          >
            <span>{expanded ? "收起拼接示例" : "展开拼接示例"}</span>
            <i className={expanded ? "ri-arrow-up-s-line text-sm" : "ri-arrow-down-s-line text-sm"} />
          </button>
        </div>

        {/* Pipeline sequence flow indicator */}
        <div className="bg-white/80 border border-slate-200/80 rounded-md px-2.5 py-1.5 text-[11px] text-slate-600 flex flex-wrap items-center gap-1 sm:gap-1.5">
          <span className="font-semibold text-slate-700 shrink-0">拼接顺序：</span>
          <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 font-medium border border-teal-200 shrink-0">
            1. 模板提示词 (需填写)
          </span>
          <i className="ri-arrow-right-s-line text-gray-400 shrink-0" />
          <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium border border-indigo-200 shrink-0">
            2. 年龄性别 (自动)
          </span>
          <i className="ri-arrow-right-s-line text-gray-400 shrink-0" />
          <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-medium border border-purple-200 shrink-0">
            3. 动作描述 (自动)
          </span>
          <i className="ri-arrow-right-s-line text-gray-400 shrink-0" />
          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-medium border border-amber-200 shrink-0">
            4. 固定提示词 (人体约束 · 固定末尾)
          </span>
        </div>

        {/* Four stages cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-0.5">
          <div className="bg-teal-50/40 p-2.5 rounded-lg border border-teal-200/80 space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-teal-900 block">1. 模板提示词</span>
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-teal-100 text-teal-800 rounded">
                当前输入框 · 仅维护此项
              </span>
            </div>
            <p className="text-[11px] text-teal-900/80 leading-relaxed">
              专注描述背景场景空间、机位景别、远近光影与服装融合契约。<strong>无需</strong>写入人物年龄性别、动作或固定人体结构约束。
            </p>
          </div>

          <div className="bg-white p-2.5 rounded-lg border border-gray-200 space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-indigo-900 block">2. 人物年龄性别提示词</span>
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                系统识别 · 自动追加
              </span>
            </div>
            <p className="text-[11px] text-gray-600 leading-relaxed">
              运行时识别人像后动态生成年龄与性别特征（如「图2人物为约28岁的女性。」），自动拼接，无需在模板中重复书写。
            </p>
          </div>

          <div className="bg-white p-2.5 rounded-lg border border-gray-200 space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-purple-900 block">3. 动作提示词</span>
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded border border-purple-100">
                动作库命中 · 自动追加
              </span>
            </div>
            <p className="text-[11px] text-gray-600 leading-relaxed">
              模板关联动作白名单后，按人物年龄性别特征匹配并随机命中追加。未配置白名单或未命中时跳过此段，不影响生成。
            </p>
          </div>

          <div className="bg-white p-2.5 rounded-lg border border-gray-200 space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-amber-900 block">4. 固定提示词（人体约束）</span>
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-800 rounded border border-amber-200/60">
                后端兜底 · 始终固定在末尾
              </span>
            </div>
            <p className="text-[11px] text-gray-600 leading-relaxed">
              固定提示词由后端统一追加，包含强制人体解剖与肢体结构约束（防多肢畸变），始终位于最终提示词末尾，全局生效且不可编辑。
            </p>
          </div>
        </div>

        {/* Contract & Fallback assurance note */}
        <div className="flex items-start gap-1.5 text-[11px] text-slate-600 bg-slate-100/80 p-2 rounded border border-slate-200/80">
          <i className="ri-information-line text-xs text-teal-600 shrink-0 mt-0.5" />
          <span>
            <strong>填写提示：</strong>系统不会修改您填写的模板文本，而后端会自动将「人物年龄性别提示词 → 动作提示词 → 固定提示词」拼接在模板提示词之后。您在此处只需专心编写场景与构图。
          </span>
        </div>

        {/* Expandable composition example */}
        {expanded && (
          <div className="mt-2 pt-2 border-t border-slate-200/80 space-y-2">
            <div className="text-[11px] font-semibold text-slate-800 flex items-center gap-1">
              <i className="ri-terminal-window-line text-teal-600" />
              万相运行时最终提示词四段拼接结构（非空部分以双换行 \n\n 串联）：
            </div>
            <div className="bg-slate-900 text-slate-100 rounded-lg p-3 font-mono text-[11px] leading-relaxed space-y-2.5 shadow-inner">
              <div className="space-y-1">
                <div className="text-teal-400 font-sans text-[10px] font-medium">
                  // [第 1 段 · 管理员维护输入] 模板提示词 (Template Prompt)
                </div>
                <div className="text-slate-200 pl-2 border-l-2 border-teal-500/50">
                  正面平视半身特写，图2的人物在图1的三峡江畔古风栈道，保持图1的烟雨水墨江景与远山构图，身着图3的汉服，衣褶与环境自然融合。
                </div>
              </div>

              <div className="space-y-1 pt-1">
                <div className="text-indigo-400 font-sans text-[10px] font-medium">
                  // [第 2 段 · 系统识别自动追加] 人物年龄性别提示词 (Demographic Fact)
                </div>
                <div className="text-slate-200 pl-2 border-l-2 border-indigo-500/50">
                  图2人物为约26岁的女性。
                </div>
              </div>

              <div className="space-y-1 pt-1">
                <div className="text-purple-400 font-sans text-[10px] font-medium">
                  // [第 3 段 · 动作库命中后自动追加] 动作提示词 (Action Prompt - 可选)
                </div>
                <div className="text-slate-200 pl-2 border-l-2 border-purple-500/50">
                  双手微合轻握身前，侧身含笑面向镜头，神态温婉宁静。
                </div>
              </div>

              <div className="space-y-1 pt-1">
                <div className="text-amber-400 font-sans text-[10px] font-medium">
                  // [第 4 段 · 后端自动追加 · 始终固定在末尾] 固定提示词（人体结构约束） (Fixed Prompt)
                </div>
                <div className="text-slate-300 pl-2 border-l-2 border-amber-500/50 text-[10.5px]">
                  人物必须是唯一、完整且符合真实人类解剖结构的人物：仅一颗头、两只眼睛、一个鼻子、一张嘴、两条手臂、两只手、两条腿和两只脚；手臂、手掌、手指、肩颈、躯干、腰胯、膝盖和脚部比例自然，关节连接正确。不得出现多余肢体、缺失肢体、重复手脚、融合手指、扭曲关节、断裂身体、异常长短的四肢、悬空肢体或不符合人体结构的姿态。
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
